import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, MessageCircle } from 'lucide-react';

const Hero = ({ onNavigate, onOpenChat }) => {
  const { t } = useLanguage();

  // Modern luxury image - infinity pool overlooking ocean
  const backgroundImage = "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=2000&q=80";

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt="Luxury Pool Bali"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-[#1A1A2E]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A2E]/80 via-[#1A1A2E]/40 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
        <div className="max-w-2xl">
          {/* Overline */}
          <p className="text-[#C9A227] text-xs font-semibold tracking-[0.2em] uppercase mb-6 animate-fade-in-up" data-testid="hero-overline">
            {t('hero.overline')}
          </p>

          {/* Main Title */}
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white font-semibold leading-tight mb-6 animate-fade-in-up stagger-1">
            {t('hero.title')}
          </h1>

          {/* Subtitle */}
          <p className="text-lg text-white/90 mb-10 leading-relaxed animate-fade-in-up stagger-2">
            {t('hero.subtitle')}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-in-up stagger-3">
            <button
              onClick={() => onNavigate('properties')}
              className="btn-gold flex items-center space-x-2"
              data-testid="hero-cta-invest"
            >
              <span>{t('hero.cta')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            
            <button
              onClick={onOpenChat}
              className="btn-outline-light flex items-center space-x-2"
              data-testid="hero-cta-chat"
            >
              <MessageCircle className="w-4 h-4" />
              <span>{t('hero.chatCta')}</span>
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-8 sm:gap-12 mt-16 pt-8 border-t border-white/20 animate-fade-in-up stagger-4">
            <div>
              <p className="text-3xl sm:text-4xl font-playfair text-[#C9A227] font-semibold">12-16%</p>
              <p className="text-white/70 text-sm mt-1">{t('hero.stats.yield')}</p>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <p className="text-3xl sm:text-4xl font-playfair text-[#C9A227] font-semibold">10%</p>
              <p className="text-white/70 text-sm mt-1">{t('hero.stats.tax')}</p>
            </div>
            <div className="w-px h-12 bg-white/20 hidden sm:block" />
            <div className="hidden sm:block">
              <p className="text-xl sm:text-2xl font-playfair text-[#C9A227] font-semibold">{t('hero.stats.project')}</p>
              <p className="text-white/70 text-sm mt-1">{t('hero.stats.projectName')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
          <div className="w-1 h-2 bg-[#C9A227] rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
